// eslint-disable-next-line @typescript-eslint/ban-ts-comment

import dayjs from "dayjs"
import { v4 as uuidv4 } from "uuid"

import { contractorUserRoleOptions } from "constants/SelectFieldOptions"
import SubscriptionPlanTitlesWithPrefix from "constants/SubscriptionPlanTitlesWithPrefix"
import { ApiTypes, UITypes } from "types"

import SubscriptionPlan from "../types/enums/SubscriptionPlan"
import { capitalizeFirstLetter } from "./capitalizeFirstLetter"
import { getProductTypeLabel } from "./getProductTypeLabel"
import decodeToMarkDown from "./markdown/decodeToMarkDown"
import { parseDateStringToUTCString } from "./parseDateStringToUTCString"
import { parseSystemType } from "./parseSystemType"
import { transformString } from "./transformString"
import { transformText } from "./transformText"

export const convertStatusIDToOrderStatus = (id: number): UITypes.OrderStatus => {
  /*
    CASE New = 1;
    CASE Pending = 2;
    CASE Denied = 3;
    CASE Confirmed = 4;
    CASE Packed = 5;
    CASE Shipping = 6;
    CASE Archived (Fulfilled) = 7;
  */

  switch (id) {
    case 1:
      return "new"
    case 2:
      return "pending"
    case 3:
      return "denied"
    case 4:
      return "confirmed"
    case 5:
      return "packed"
    case 6:
      return "shipping"
    case 7:
      return "fulfilled"
    default:
      return ""
  }
}

export const convertStatusesToOptions = (data?: ApiTypes.Res.Status[]): UITypes.Option[] => {
  if (!data?.length) return []

  return data.map(item => {
    return {
      value: String(item.id),
      label: item.name,
    }
  })
}

export const convertDataToOptions = <T extends Record<Y | Z, string | number>, Y extends keyof T, Z extends keyof T>(
  data: { data: T[] | null } | undefined,
  valueName: Y,
  labelName: Z,
): UITypes.Option[] => {
  if (!data?.data?.length) return []

  return data.data.map((item: T) => ({
    value: item && item[valueName],
    label: item && item[labelName]?.toString(),
  }))
}

export const convertStringArrayToOptions = (data: string[]): UITypes.Option[] => [
  ...data.map(item => ({
    value: item,
    label: item,
  })),
]

// TABLES
export const convertVSMOrdersToTableRows = (data?: ApiTypes.Model.VSManagerOrder[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []
  return data.map(
    ({
      id,
      create_date = "",
      quantity,
      status,
      email,
      phone_number,
      company_name,
      warehouse,
      status_updated,
      shipping_date,
      fulfillment_date,
    }) =>
      ({
        checkbox: {},
        id: { value: id },
        status: { value: status },
        company_name: { value: company_name },
        create_date: { value: create_date ? dayjs(create_date).format("YYYY-MM-DD") : undefined },
        shipping_date: { value: shipping_date ? dayjs(shipping_date).format("YYYY-MM-DD") : undefined },
        fulfillment_date: { value: fulfillment_date ? dayjs(fulfillment_date).format("YYYY-MM-DD") : undefined },
        quantity: { value: quantity },
        warehouse: { value: warehouse },
        phone_number: { value: phone_number },
        email: { value: email },
        status_updated: { value: status_updated ? dayjs(status_updated).format("YYYY-MM-DD") : undefined },
        rowSettings: {
          id,
          status,
          isEnable: status === "new" ? true : false,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertVSMOrdersNewToTableRows = (data?: ApiTypes.Model.VSManagerOrderNew[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []
  return data.map(({ id, create_date = "", status, email, phone_number, company_name, items, contractor_id }) => {
    const singleItem = items.length === 1 ? items[0] : null

    return {
      group_toggle: { hidden: !!singleItem },
      item_id: { value: singleItem?.id },
      item_status: { value: status, csvValue: singleItem?.status },
      company_name: { value: company_name },
      create_date: { value: create_date ? dayjs(create_date).format("YYYY-MM-DD") : undefined },
      shipping_date: {
        value: singleItem?.shipping_date ? dayjs(singleItem?.shipping_date).format("YYYY-MM-DD") : undefined,
      },
      fulfillment_date: {
        value: singleItem?.fulfillment_date ? dayjs(singleItem?.fulfillment_date).format("YYYY-MM-DD") : undefined,
      },
      product_type: { value: getProductTypeLabel(singleItem?.product_type) },
      quantity: { value: singleItem?.quantity },
      item_type: {
        value: singleItem?.item_type ? transformText(singleItem.item_type) : undefined,
      },
      warehouse: { value: singleItem?.warehouse },
      phone_number: { value: phone_number },
      email: { value: email },
      contractor_id: { value: contractor_id },
      status_updated: {
        value: singleItem?.status_updated ? dayjs(singleItem?.status_updated).format("YYYY-MM-DD") : undefined,
      },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            contractor_id: { value: "" },
            item_status: { value: status },
            company_name: { value: "" },
            item_id: { value: item.id },
            create_date: { value: null },
            shipping_date: { value: item.shipping_date ? dayjs(item.shipping_date).format("YYYY-MM-DD") : undefined },
            fulfillment_date: {
              value: item.fulfillment_date ? dayjs(item.fulfillment_date).format("YYYY-MM-DD") : undefined,
            },
            product_type: { value: getProductTypeLabel(item.product_type) },
            quantity: { value: item.quantity },
            item_type: {
              value: item.item_type ? transformText(item.item_type) : undefined,
            },
            warehouse: { value: item.warehouse },
            phone_number: { value: "" },
            email: { value: "" },
            status_updated: {
              value: item?.status_updated ? dayjs(item?.status_updated).format("YYYY-MM-DD") : undefined,
            },
            rowSettings: { id: item.id, item: [item] },
          })),
      rowSettings: {
        id,
        item: items,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertOrdersToTableRows = (data?: ApiTypes.Model.Order[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, create_date, quantity, warehouse, status, shipping_date, fulfillment_date }) => ({
    checkbox: {},
    id: { value: id },
    group_toggle: {},
    create_date: { value: dayjs(create_date).format("YYYY-MM-DD") },
    shipping_date: { value: shipping_date ? dayjs(shipping_date).format("YYYY-MM-DD") : undefined },
    fulfillment_date: { value: fulfillment_date ? dayjs(fulfillment_date).format("YYYY-MM-DD") : undefined },
    quantity: { value: quantity },
    status: { value: status },
    warehouse: { value: warehouse },
    rowSettings: {
      id,
      status: status,
    },
  }))
}

export const convertOrdersNewToTableRows = (data?: ApiTypes.Model.OrderNew[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, items, create_date, status }) => {
    const singleItem = items.length === 1 ? items[0] : null

    return {
      item_id: { value: singleItem?.id },
      group_toggle: { hidden: !!singleItem },
      create_date: { value: dayjs(create_date).format("YYYY-MM-DD") },
      shipping_date: {
        value: singleItem?.shipping_date ? dayjs(singleItem?.shipping_date).format("YYYY-MM-DD") : undefined,
      },
      fulfillment_date: {
        value: singleItem?.fulfillment_date ? dayjs(singleItem?.fulfillment_date).format("YYYY-MM-DD") : undefined,
      },
      quantity: { value: singleItem?.quantity },
      item_type: {
        value: singleItem?.item_type ? transformText(singleItem.item_type) : undefined,
      },
      warehouse: {
        value: singleItem?.warehouse,
      },
      item_status: { value: status, csvValue: singleItem?.status },
      product_type: { value: getProductTypeLabel(singleItem?.product_type) },
      rowSettings: {
        id,
        item: items,
      },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            item_id: { value: item.id },
            create_date: { value: null },
            shipping_date: { value: item.shipping_date ? dayjs(item.shipping_date).format("YYYY-MM-DD") : undefined },
            fulfillment_date: {
              value: item.fulfillment_date ? dayjs(item.fulfillment_date).format("YYYY-MM-DD") : undefined,
            },
            quantity: { value: item.quantity },
            item_type: {
              value: item.item_type ? transformText(item.item_type) : undefined,
            },
            warehouse: {
              value: item.warehouse,
            },
            item_status: { value: item.status },
            product_type: { value: getProductTypeLabel(item.product_type) },
            rowSettings: {
              id: item.id,
              item: [item],
            },
          })),
    }
  })
}

export const convertClientsToTableRows = (data?: ApiTypes.Model.Client[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, full_name, devices, email, phone_number, membership, notes, app_activated }) =>
      ({
        checkbox: {},
        email: { value: email },
        devices_count: { value: devices },
        phone: { value: phone_number },
        full_name: { value: full_name },
        id: { value: id },
        membership: { value: membership },
        notes: { value: notes || "" },
        app_activated: { value: app_activated ? "yes" : "no" },
        rowSettings: {
          id,
          isEnable: devices ? true : false,
          item: {
            client_id: id,
            devices_count: devices,
          },
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertContractorsToTableRows = (data?: ApiTypes.Model.Contractor[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    contractor =>
      ({
        name: { value: contractor.name },
        state: { value: contractor.state?.name },
        country: { value: contractor.country?.name },
        subscription_plan: {
          value: SubscriptionPlanTitlesWithPrefix[contractor.subscription_plan || SubscriptionPlan.BASIC],
        },
        brand: { value: contractor.brand },
        purchased: { value: contractor.purchased_devices },
        installed: { value: contractor.installed_devices },
        install_7_day: { value: contractor.install_7_day },
        active_mobile: { value: contractor.active_mobile },
        inventory: { value: contractor.inventory_devices },
        isEnabled: { value: contractor.is_enabled ? "1" : "0" },
        account_status_id: { value: contractor.account_status ? String(contractor.account_status.id) : "" },
        warranty: { value: contractor.warranty },
        create_date: { value: contractor.created_at ? dayjs(contractor.created_at).format("YYYY-MM-DD") : undefined },
        rowSettings: {
          id: Number(contractor.id),
          isEnabled: contractor.is_enabled,
          item: contractor,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertAdminAlertsToTableRows = (data?: ApiTypes.Model.Alert[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, sn, country, address1, city, state, date, status, type, full_name, phone_number, email, location }) =>
      ({
        checkbox: {},
        type: { value: transformText(type) },
        date: { value: date },
        sn: { value: sn },
        status: { value: status },
        full_name: { value: full_name },
        phone_number: { value: phone_number },
        email: { value: email },
        location: { value: location },
        country: { value: country },
        address1: { value: address1 },
        city: { value: city },
        state: { value: state },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertAlertsToTableRows = (data?: ApiTypes.Model.Alert[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      sn,
      country,
      address1,
      city,
      state,
      date,
      datetime,
      status,
      type,
      full_name,
      phone_number,
      email,
      location,
    }) =>
      ({
        checkbox: {},
        type: { value: transformText(type) },
        date: { value: date },
        sn: { value: sn },
        status: { value: status },
        full_name: { value: full_name },
        phone_number: { value: phone_number },
        email: { value: email },
        location: { value: location },
        country: { value: country },
        address1: { value: address1 },
        city: { value: city },
        state: { value: state },
        datetime: { value: datetime },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertMessageTemplatesToTableRows = (data?: ApiTypes.Model.MessageTemplate[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, title, content_rich, created_at }) => ({
    title: { value: title },
    created_at: { value: dayjs(created_at).format("MM-DD-YYYY") },
    message: { value: content_rich ? decodeToMarkDown(content_rich) : "" },
    id: { value: id },
    rowSettings: {
      id,
    },
  }))
}

export const convertMessageTemplatesToTableRowsOld = (
  data?: ApiTypes.Model.MessageTemplateOld[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, title, body_rich, created_at }) => ({
    title: { value: title },
    created_at: { value: dayjs(created_at).format("MM-DD-YYYY") },
    message: { value: body_rich ? decodeToMarkDown(body_rich) : "" },
    id: { value: id },
    rowSettings: {
      id,
    },
  }))
}

export const convertTemplatesLibraryToTableRows = (
  data?: ApiTypes.Model.TemplatesLibrary[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data?.map(group => ({
    id: { value: group?.name, groupHeader: true },
    title: { value: "", displayNone: true },
    message: { value: "", displayNone: true },
    rowSettings: {
      id: group?.id + 1000,
      isEnable: false,
    },
    subRows:
      group?.items?.map(items => ({
        id: { value: items?.id },
        title: { value: items?.title },
        message: { value: items?.description_rich ? decodeToMarkDown(items?.description_rich) : "" },
        rowSettings: {
          id: items?.id,
        },
      })) || [],
  }))
}

export const convertWarehousesToTableRows = (data?: ApiTypes.Model.Warehouse[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, name, address, phone_number, zip, state, devices_qty, managers }) =>
      ({
        name: { value: name },
        devices_qty: { value: devices_qty },
        address: { value: address },
        zip: { value: zip?.code },
        state: { value: state?.name },
        phone_number: { value: phone_number },
        manager: { value: managers },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDevicesAndScheduledMessagesToTableRows = (
  data?: ApiTypes.Model.Device[],
  devicesMessagesData?: ApiTypes.Model.DevicesMessages[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(device => {
    const deviceMessages = devicesMessagesData?.find(item => item?.device_id === device.id)
    const singleMessage = deviceMessages?.messages?.length === 1 ? deviceMessages?.messages[0] : null

    return {
      group_toggle: { hidden: singleMessage || !deviceMessages?.messages?.length ? true : false },
      checkbox: {},
      email: { value: device.email },
      sn: { value: device.sn },
      country: { value: device.country?.name },
      city: { value: device.city?.name },
      zip_code: { value: device.zip?.code },
      state: { value: device.state?.name },
      option: { value: device.option ? capitalizeFirstLetter(device.option).replace("_", "-") : "" },
      activation_date: { value: device.activation_date },
      homeowner_name: { value: device.homeowner_name },
      membership: { value: device.membership },
      membership_name: { value: device.memberships },
      residence_type: { value: device.residence_type?.name },
      system_type: { value: device.system_type },
      accessories: { value: device.accessories },
      installation_type: { value: device.hvac },
      system_age: { value: device.system_age },
      schedule: { value: singleMessage?.scheduled_at },
      scheduled_message: { value: singleMessage?.message_template_id },
      subRows:
        deviceMessages && !singleMessage
          ? deviceMessages.messages.map(message => ({
              group_toggle: { hidden: true },
              checkbox: { hidden: true },
              email: {},
              sn: {},
              country: {},
              city: {},
              zip_code: {},
              state: {},
              option: {},
              activation_date: {},
              homeowner_name: {},
              membership: {},
              membership_name: {},
              residence_type: {},
              system_type: {},
              accessories: {},
              installation_type: {},
              system_age: {},
              schedule: { value: message?.scheduled_at },
              scheduled_message: { value: message?.message_template_id },
              rowSettings: {
                id: message?.message_id,
                item: message,
              },
            }))
          : [],
      rowSettings: {
        id: device.id,
        item: {
          ...singleMessage,
          client_id: device.client_id,
          sn: device.sn,
          email: device.email,
          phisical_status: device.phisical_status,
        },
      },
    } as UITypes.TableBodyRow
  })
}

export const convertDevicesToTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        email: { value: device.email },
        sn: { value: device.sn },
        country: { value: device.country?.name },
        city: { value: device.city?.name },
        zip_code: { value: device.zip?.code },
        state: { value: device.state?.name },
        option: { value: device.option ? capitalizeFirstLetter(device.option).replace("_", "-") : "" },
        activation_date: { value: device.activation_date },
        homeowner_name: { value: device.homeowner_name },
        membership: { value: device.membership },
        membership_name: { value: device.memberships },
        residence_type: { value: device.residence_type?.name },
        system_type: { value: device.system_type },
        accessories: { value: device.accessories },
        installation_type: { value: device.hvac },
        system_age: { value: device.system_age },
        rowSettings: {
          id: device.id,
          item: device,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDevicesToDashboardTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        sn: { value: device.sn },
        phisical_status: {
          value: device.phisical_status,
        },
        email: { value: device.email },
        homeowner_name: { value: device.homeowner_name },
        country: { value: device.country?.name },
        zip_code: { value: device.zip?.code },
        activation_date: { value: device.activation_date },
        address: {
          value: `${device.address ? device.address : ""} ${device.state?.name ? device.state?.name : ""} ${device.city?.name ? device.city?.name : ""}`,
        },
        installation_type: { value: device.hvac },
        alerts: { value: device.alerts },
        membership: { value: device.membership },
        membership_name: { value: device.memberships },
        system_type: { value: parseSystemType(device.system_type) },
        accessories: { value: device.accessories },
        system_age: { value: device.system_age },
        rowSettings: {
          id: device.id,
          item: device,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDataToPerformanceTestTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(device => {
    const {
      id,
      email,
      sn,
      perftest,
      homeowner_name,
      country,
      city,
      state,
      zip,
      work_status,
      address,
      hvac,
      alerts,
      membership,
      memberships,
      system_type,
      accessories,
      system_age,
    } = device

    return {
      checkbox: { disabled: perftest?.status?.toLowerCase() === "running" },
      email: { value: email },
      perftest_status: { value: perftest?.status?.toLowerCase() },
      temp_change: { value: { start_temp: perftest?.start, end_temp: perftest?.end } },
      work_status: { value: work_status },
      perftest_label: { value: perftest?.label },
      country: { value: country?.name },
      city: { value: city?.name },
      zip_code: { value: zip?.code },
      state: { value: state?.name },
      homeowner_name: { value: homeowner_name },
      sn: { value: sn },
      address: { value: address || "" },
      installation_type: { value: hvac },
      alerts: { value: alerts },
      membership: { value: membership },
      membership_name: { value: memberships },
      system_type: { value: parseSystemType(system_type) },
      accessories: { value: accessories },
      perftest_schedule: { value: perftest?.schedule },
      system_age: { value: system_age },
      rowSettings: {
        id,
        item: device,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertDataToPerformanceTestHistoryTableRows = (
  data?: ApiTypes.Model.PerformanceTestHistory[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ client, device, perftest }) =>
      ({
        email: { value: client?.email },
        status: { value: perftest?.status?.toLowerCase() },
        temp_change: { value: { start_temp: perftest?.start, end_temp: perftest?.end } },
        time: { value: dayjs(parseDateStringToUTCString(perftest.time)).format("YYYY-MM-DD") },
        label: { value: perftest?.label },
        country: { value: device?.country?.name },
        city: { value: device?.city?.name },
        zip_code: { value: device?.zip?.code },
        state: { value: device?.state?.name },
        homeowner_name: { value: client?.homeowner_name },
        sn: { value: device?.sn },
        activation_date: { value: device?.activation_date },
        address: { value: device?.address || "" },
        system_age: { value: device?.system_age },
        rowSettings: {
          isRowClickable: true,
          id: perftest?.perftest_result_id,
          item: { perftest, device, client_id: client?.id, phisical_status: "installed", sn: device?.sn },
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDataToMonitoringTableRows = (data?: ApiTypes.Model.Device[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(device => {
    const {
      id,
      email,
      perftest,
      sn,
      homeowner_name,
      country,
      city,
      state,
      zip,
      work_status,
      name,
      address,
      hvac,
      alerts,
      membership,
      memberships,
      system_type,
      accessories,
      system_age,
    } = device

    const perfTestStatus = perftest?.status?.toLowerCase()

    return {
      email: { value: email },
      perftest_status: { value: ["passed", "issue", "running"].includes(perfTestStatus) ? perfTestStatus : null },
      work_status: { value: work_status },
      country: { value: country?.name },
      city: { value: city?.name },
      zip_code: { value: zip?.code },
      state: { value: state?.name },
      homeowner_name: { value: homeowner_name },
      sn: { value: sn },
      name: { value: name },
      address: { value: address || "" },
      installation_type: { value: hvac },
      alerts: { value: alerts },
      membership: { value: membership },
      membership_name: { value: memberships },
      system_type: { value: parseSystemType(system_type) },
      accessories: { value: accessories },
      system_age: { value: system_age },
      rowSettings: {
        id,
        item: device,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertMessagesBatchToTableRows = (data?: ApiTypes.Model.MessageBatch[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      title,
      content_rich,
      created_at,
      most_recent_delivery_timestamp,
      scheduled_delivery_timestamp,
      pending_delivery_count,
      failed_delivery_count,
      delivered_count,
    }) =>
      ({
        title: { value: title },
        content: { value: content_rich ? decodeToMarkDown(content_rich) : "" },
        date: { value: created_at ? dayjs(created_at).format("YYYY-MM-DD HH:mm:ss") : undefined },
        most_recent_delivery_date: {
          value: most_recent_delivery_timestamp
            ? dayjs(most_recent_delivery_timestamp).format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        },
        scheduled_delivery_date: {
          value: scheduled_delivery_timestamp
            ? dayjs(scheduled_delivery_timestamp).format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        },
        delivered: { value: delivered_count },
        pending: { value: pending_delivery_count },
        failed: { value: failed_delivery_count },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertMessagesHistoryToTableRows = (data?: ApiTypes.Model.MessageHistory[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, title, body_rich, pending, failed, delivered, date }) =>
      ({
        title: { value: title },
        body: { value: body_rich ? decodeToMarkDown(body_rich) : "" },
        date: { value: date },
        delivered: { value: delivered },
        pending: { value: pending },
        failed: { value: failed },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertWHManagerOrdersToTableRows = (data?: ApiTypes.Model.WHManagerOrder[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, contractor_id, order_quantity, status, company_name, brand_name }) =>
      ({
        order_number: { value: id },
        status: { value: status },
        company_name: { value: company_name },
        brand_name: { value: brand_name },
        order_quantity: { value: order_quantity },
        contractor_id: { value: contractor_id },
        rowSettings: {
          id,
          status,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertWHManagerOrdersNewToTableRows = (
  data?: ApiTypes.Model.WHManagerOrderNew[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(({ id, contractor_id, items, company_name, brand_name }) => {
    const singleItem = items.length === 1 ? items[0] : null

    return {
      group_toggle: { hidden: !!singleItem },
      item_id: { value: singleItem?.id },
      item_status: { value: singleItem?.status },
      company_name: { value: company_name },
      brand_name: { value: brand_name },
      quantity: { value: singleItem?.quantity },
      product_type: { value: getProductTypeLabel(singleItem?.product_type) },
      contractor_id: { value: contractor_id },
      subRows: singleItem
        ? []
        : items.map(item => ({
            group_toggle: { hidden: true },
            item_id: { value: item.id },
            item_status: { value: item.status },
            quantity: { value: item.quantity },
            product_type: { value: getProductTypeLabel(item.product_type) },
            brand_name: { value: null },
            company_name: { value: null },
            contractor_id: { value: null },
            rowSettings: {
              id: item.id,
              item: [item],
            },
          })),
      rowSettings: {
        id,
        item: items,
      },
    } as UITypes.TableBodyRow
  })
}

export const convertWHManagerDevicessToTableRows = (
  data?: ApiTypes.Model.WHManagerDevice[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, sn, input_date, phisical_status }) =>
      ({
        checkbox: {},
        sn: { value: sn },
        input_date: { value: input_date },
        phisical_status: { value: phisical_status },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertMessageReportToTableRows = (data?: ApiTypes.Model.MessageReportV2[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      sn,
      client_name,
      country_name,
      city_name,
      zip_code,
      state_name,
      activation_date,
      membership,
      residence_type,
      installation_type,
      accessories,
      system_type,
      system_age,
      messages,
    }) => {
      const singleMessage = messages.length === 1 ? messages[0] : null

      return {
        group_toggle: { hidden: singleMessage || !messages.length ? true : false },
        sn: { value: sn },
        client_name: { value: client_name },
        country: { value: country_name },
        city: { value: city_name },
        zip: { value: zip_code },
        state: { value: state_name },
        activation_date: { value: activation_date },
        membership: { value: membership },
        residence_type: { value: residence_type },
        system_type: { value: system_type },
        accessories: { value: accessories },
        installation_type: { value: installation_type },
        delivery_status: { value: singleMessage?.delivery_status },
        system_age: { value: system_age },
        scheduled_at: {
          value: singleMessage?.scheduled_at
            ? dayjs(singleMessage?.scheduled_at).format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        },
        delivered_at: {
          value: singleMessage?.delivered_at
            ? dayjs(singleMessage?.delivered_at).format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        },
        subRows: !singleMessage
          ? messages.map(message => ({
              group_toggle: { hidden: true },
              sn: { hidden: true },
              client_name: { hidden: true },
              country: { hidden: true },
              city: { hidden: true },
              zip: { hidden: true },
              state: { hidden: true },
              activation_date: { hidden: true },
              membership: { hidden: true },
              residence_type: { hidden: true },
              system_type: { hidden: true },
              accessories: { hidden: true },
              installation_type: { hidden: true },
              delivery_status: { value: message.delivery_status },
              system_age: { hidden: true },
              scheduled_at: {
                value: message.scheduled_at ? dayjs(message.scheduled_at).format("YYYY-MM-DD HH:mm:ss") : undefined,
              },
              delivered_at: {
                value: message.delivered_at ? dayjs(message.delivered_at).format("YYYY-MM-DD HH:mm:ss") : undefined,
              },
              rowSettings: {
                id: message.id,
              },
            }))
          : [],
        rowSettings: {
          id,
        },
      } as UITypes.TableBodyRow
    },
  )
}

export const convertMessageReportToTableRowsOld = (
  data?: ApiTypes.Model.MessageReportOld[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({
      id,
      device_sn,
      client_name,
      country,
      city,
      zip_code,
      state,
      activation_date,
      membership,
      residence_type,
      installation_type,
      accessories,
      status,
      system_type,
      system_age,
    }) =>
      ({
        device_sn: { value: device_sn },
        client_name: { value: client_name },
        country: { value: country },
        city: { value: city },
        zip: { value: zip_code },
        state: { value: state },
        activation_date: { value: activation_date },
        membership: { value: membership },
        residence_type: { value: residence_type },
        system_type: { value: system_type },
        accessories: { value: accessories },
        installation_type: { value: installation_type },
        status: { value: status },
        system_age: { value: system_age },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertUsersToTableRows = (data?: ApiTypes.Model.User[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, email, full_name, phone, role, created_by, creation_date }) =>
      ({
        name: { value: full_name },
        role: { value: transformString(role?.type) },
        email: { value: email },
        phone: { value: phone },
        creation_date: { value: creation_date },
        created_by: { value: created_by },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertContractorUsersToTableRows = (data?: ApiTypes.Model.User[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    ({ id, email, full_name, phone, role, is_enabled, created_by, creation_date }) =>
      ({
        name: { value: full_name },
        role: { value: contractorUserRoleOptions.find(option => option.value === role?.type)?.label || "" },
        email: { value: email },
        phone: { value: phone },
        creation_date: { value: creation_date },
        created_by: { value: created_by },
        is_enabled: { value: is_enabled },
        rowSettings: {
          id,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertValidatedDevicesToState = (data: ApiTypes.Model.InvalideDevice[]): UITypes.InvalidDevice[] => {
  if (!data?.length) return []

  return data.map((item, index) => ({
    sn: item?.sn ? item.sn : "",
    row: item?.row ? item.row : (index += 1),
    uid: item?.uid ? item.uid : uuidv4(),
    errorMessage: item?.error_message ? item?.error_message : "",
  }))
}

export const convertVendorDevicesToTableRows = (
  data?: ApiTypes.Model.VendorDevice[],
  headerCells?: UITypes.TableHeaderCell[],
  callback?: (id: number) => void,
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        sn: { value: device.sn, callback: () => callback && callback(device.id) },
        work_status: { value: device.work_status },
        phisical_status: { value: device.phisical_status },
        contractor_name: { value: device.contractor_name },
        activation_date: { value: device.activation_date },
        red_flag: { value: `${device.red_flag ? "Yes" : "No"}` },
        red_flag_type: { value: device.red_flag_type },
        homeowner: { value: device.homeowner },
        country: { value: device.country_name },
        address: { value: device.address },
        email: { value: device.email },
        phone: { value: device.phone },
        installation_type: { value: device.installation_type },
        device_location: { value: device.device_location },
        system_age: { value: device.system_age },
        rowSettings: {
          id: Number(device.id),
          isRedFlag: device.red_flag,
          item: device,
        },
      }) as UITypes.TableBodyRow,
  )
}

export const convertDeviceHistoryLog = (data: ApiTypes.Model.HistoryLog[]): UITypes.HistoryLog[] => {
  if (!data?.length) return []

  return data?.map(item => ({
    status: item?.status,
    author: item?.author,
    lastUpdate: item?.last_update,
  }))
}

export const convertEditDevicesToState = (data: ApiTypes.Model.Device[]): UITypes.Device[] => {
  if (!data?.length) return []

  const checkInstallationType = (value: string | undefined) => {
    switch (value) {
      case "new":
        return [
          {
            value: "new",
            label: "New",
          },
        ]
      case "existing":
        return [
          {
            value: "existing",
            label: "Existing",
          },
        ]
      default:
        return []
    }
  }

  return data?.map(item => ({
    _id: uuidv4(),
    id: item?.id ? item?.id : undefined,
    serialNumber: item?.sn ? item?.sn : "",
    address1: item?.address1 ? item?.address1 : "",
    address2: item?.address2 ? item?.address2 : "",
    country: convertDataToOptions({ data: [item?.country] }, "id", "name"),
    zip: convertDataToOptions({ data: [item?.zip] }, "id", "code"),
    state: convertDataToOptions({ data: [item?.state] }, "id", "name"),
    city: convertDataToOptions({ data: [item?.city] }, "id", "name"),
    suburb: convertDataToOptions({ data: [item?.suburb] }, "id", "name"),
    residentialType: item?.residence_type
      ? convertDataToOptions({ data: [item?.residence_type] }, "id", "name")
      : undefined,
    deviceLocation: convertDataToOptions({ data: [item?.location] }, "id", "name"),
    deviceName: item?.name || "",
    installationType: checkInstallationType(item.installation_type),
    workStatus: item?.work_status as UITypes.DeviceWorkStatus,
    systemAge: item?.system_age
      ? [
          {
            value: item?.system_age,
            label: `${item?.system_age} ${Number(item?.system_age) === 1 ? "Year" : "Years"}`,
          },
        ]
      : undefined,
  }))
}
